import {GridColDef} from '@mui/x-data-grid';
import {getEarnedStatements, DEFAULT_SORT_FIELD_EARNED_STATEMENTS} from 'Agent/Commissions/ducks/a360Commissions';
import React from 'react';
import {A360CommissionsGrid} from './CommissionsGrid';
import {A360EarnedStatement} from '../types';
import {useDatagridState} from './useDatagridState';
import {A360CommissionSection} from '../CommissionSection';
import {currencyValueFormatter, extractProducerCodes, revertNoEmployerToEmpty} from '../utilities';

const earnedStatementColumns: GridColDef[] = [
  {field: 'statementDate', headerName: 'Commission Statement Date', hide: true, width: 250},
  {field: 'employerId', headerName: 'Employer #', width: 150},
  {field: 'employerName', headerName: 'Employer Name', width: 200},
  {field: 'masterPolicyId', headerName: 'Master Policy #', width: 180},
  {field: 'productName', headerName: 'Product Name', width: 150},
  {field: 'productCode', headerName: 'Product Code', hide: true, width: 150},
  {field: 'billingAccountId', headerName: 'Billing Account #', hide: true, width: 150},
  {field: 'billingAccountType', headerName: 'Billing Account Type', hide: true, width: 150},
  {field: 'billingStartDate', headerName: 'Billing Start Date', hide: true, width: 150},
  {field: 'billingEndDate', headerName: 'Billing End Date', hide: true, width: 150},
  {field: 'certificateId', headerName: 'Certificate #', width: 150},
  {field: 'insuredName', headerName: 'Insured Name', width: 200},
  {field: 'certificateEffectiveDate', headerName: 'Certificate Effective Date', hide: true, width: 200},
  {field: 'paymentProcessDate', headerName: 'Payment Process Date', hide: true, width: 200},
  {field: 'premiumApplied', headerName: 'Premium Applied', width: 150, valueFormatter: currencyValueFormatter},
  {field: 'premiumYear', headerName: 'Premium Year', hide: true, width: 150},
  {field: 'premiumSplitPercentage', headerName: 'Premium Split Percentage', hide: true, width: 200},
  {
    field: 'commissionablePremium',
    headerName: 'Commissionable Premium',
    width: 200,
    valueFormatter: currencyValueFormatter,
  },
  {field: 'commissionType', headerName: 'Commission Type', hide: true, width: 150},
  {field: 'commissionRate', headerName: 'Commission Rate', hide: true, width: 150},
  {
    field: 'commissionGenerated',
    headerName: 'Commission Generated',
    width: 180,
    valueFormatter: currencyValueFormatter,
  },
  {field: 'advanceRecovery', headerName: 'Advance Recovery', width: 150, valueFormatter: currencyValueFormatter},
  {field: 'advanceBalance', headerName: 'Advance Balance', hide: true, width: 150},
  {
    field: 'netEarnedCommission',
    headerName: 'Net Earned Commission',
    width: 180,
    valueFormatter: currencyValueFormatter,
  },
  {field: 'productionType', headerName: 'Production Type', hide: true, width: 180},
  {field: 'producerCode', headerName: 'Producer Code', hide: true, width: 180},
  {field: 'producerName', headerName: 'Producer Name', hide: true, width: 180},
  {field: 'commissionStyle', headerName: 'Commission Style', hide: true, width: 150},
  {field: 'premiumSequenceEffectiveDate', headerName: 'Premium Sequence Effective Date', hide: true, width: 200},
].map(col => ({
  ...col,
  filterable: false,
  headerClassName: 'a360-commissions-grid__header',
}));

const getEarnedStatementsRowId = (row: A360EarnedStatement) =>
  row?.commissionStatementEarnedId ??
  `${row.advanceBalance}-${row.advanceRecovery}-${row.billingAccountId}-${row.billingAccountType}-${row.billingEndDate}-${row.billingStartDate}-${row.certificateEffectiveDate}-${row.certificateId}-${row.commissionGenerated}-${row.commissionRate}-${row.commissionType}-${row.commissionablePremium}-${row.employerId}-${row.employerName}-${row.insuredName}-${row.masterPolicyId}-${row.netEarnedCommission}-${row.paymentProcessDate}-${row.premiumApplied}-${row.premiumSplitPercentage}-${row.premiumYear}-${row.producerCode}-${row.producerName}-${row.productCode}-${row.productName}-${row.productionType}-${row.statementDate}-${row.commissionStyle}-${row.premiumSequenceEffectiveDate}`;

export const EarnedStatements = () => {
  const {filter, error, rows, loading, totalCount, handleFetch} = useDatagridState<A360EarnedStatement>(
    'earnedStatements',
    getEarnedStatements,
    DEFAULT_SORT_FIELD_EARNED_STATEMENTS
  );

  return (
    <A360CommissionSection className="a360-commissions__earned-statements" aria-label="earned statements">
      <A360CommissionsGrid<A360EarnedStatement>
        columns={earnedStatementColumns}
        error={error}
        getRowId={getEarnedStatementsRowId}
        rows={rows}
        loading={loading}
        totalRecords={totalCount}
        onFetch={handleFetch}
        initialSort={[{field: DEFAULT_SORT_FIELD_EARNED_STATEMENTS, sort: 'desc'}]}
        title="Earned Statements"
        toolbarProps={{
          exportToCsv: {
            requestPayload: {
              agencyCodes: extractProducerCodes(filter.selectedProducerCodes),
              statementDates: filter.selectedStatementDates,
              employerNames: revertNoEmployerToEmpty(filter.selectedEmployerNames),
              sortFields: [DEFAULT_SORT_FIELD_EARNED_STATEMENTS],
            },
            downloadedFileName: 'EarnedStatements',
            relativeFileLocation: '/api/commissions/v1/commissionStatements/earnedStatementSummary/csv',
          },
        }}
      />
    </A360CommissionSection>
  );
};
